import * as R from 'ramda';
import axios, { AxiosError, AxiosResponse } from 'axios';
import RetryHandler from '../common/networkRetries/retryHandler';
import { ValidationService, ValidationStep, ValidationStepStatus } from './validationService';

export default class RetryPatchValidationHandler implements RetryHandler {
  private validationService: ValidationService;

  constructor(validationService: ValidationService) {
    this.validationService = validationService;
  }

  canHandle(error: AxiosError): boolean {
    const { retryConfig } = error.config;
    if (
      R.isNil(retryConfig) ||
      error.config.method !== 'patch' ||
      R.isNil(retryConfig?.validationId) ||
      R.isNil(retryConfig?.validationStep)
    ) {
      return false;
    }

    return true;
  }

  // If the network error was on a PATCH of a validation step we need to refetch the step, inspect it and redo the call if required.
  async retry(error: AxiosError): Promise<AxiosResponse> {
    const { retryConfig } = error.config;
    let validationStep: ValidationStep;
    if (Object.values(ValidationStep).some((step: string) => step === retryConfig?.validationStep)) {
      validationStep = <ValidationStep>retryConfig?.validationStep;
    } else {
      return Promise.reject(error);
    }

    // Get the step status
    const stepStatus = await this.validationService.getStep({
      validationId: retryConfig?.validationId || '',
      step: validationStep,
    });

    if (stepStatus.isSuccessful()) {
      const { status } = stepStatus.success();
      // If the patch was accepted or rejected, use the step result and move on
      if (status === ValidationStepStatus.accepted || status === ValidationStepStatus.invalid) {
        return Promise.resolve({
          data: stepStatus.success(),
          config: {},
          headers: {},
          status: 200,
          statusText: 'OK',
        });
      }

      // Else we need to retry the patch
      return axios.request(error.config);
    }

    // if getting the step failed then reject the original call
    return Promise.reject(error);
  }
}
